import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// import { getUserDomainCompanyImages } from '@/store/users/actions';
import { getLanguagesFiltered } from '@/store/locales/actions';
import { getSearch } from '@/store/search/actions';
import {
  changeTopicFilter,
  clearTopicFilter,
  /*getTopicCategories, */ clearTopics,
  getTopicLevel,
  getTopics,
  getTopicTypes,
} from '@/store/topics/actions';

import { selectLocales } from '@/store/locales/selectors';
import { selectSearchUnion } from '@/store/search/selectors';
import { selectTopics, selectTopicsCategoriesUnion } from '@/store/topics/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Layout, Pagination, Typography } from 'antd';
import css from '@/pages/CollectionMaterials/CollectionMaterials.module.scss';
import ContextSearch from '@/components/ContextSearch';
import { CustomRangeDatePicker } from '@/components/DatePicker/AntDatePicker';
import TopicCards from '@/components/TopicCards';

import TopicFilter from '../TopicFilter';

import Sort from '@shared/components/NewSort';
import { SortTypeHelp } from '@shared/components/NewSort/constants';
import { parseSearchParams } from '@shared/utils/get-query-string';

import Utils from '@/Utils';
import _debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

import { FILTER_TYPE_COURSES, FILTER_TYPE_EVENTS } from '@/constants/file-types';

const { Content } = Layout;
const { Title } = Typography;

const initialTopicsParams = {
  size: 19,
  page: 1,
  search: '',
  searchName: '',
  skillIds: '',
  sort: 'positions.position,asc&sort=startDate,asc&sort=id,asc',
  hasEvent: true,
  actual: 'true',
  startDate: '',
  endDate: '',
};

const initialSearchTopics = {
  authorName: '',
  publisher: '',
  tagName: '',
  skillName: '',
  name: '',
  searchResult: '',
  tagId: '',
  authorId: '',
  skillId: '',
};

const initialTopicFilters = {
  topicTypeId: '',
  typeId: undefined,
  topicLevelId: undefined,
  languageId: undefined,
  free: '',
  hasBlock: '',
  topicSubTypeId: '',
};

const initialAllState = {
  ...initialTopicsParams,
  ...initialSearchTopics,
  ...initialTopicFilters,
};

let controllerTopics = null;

const CalendarMaterials = props => {
  const { t } = useTranslation('collectionMaterials');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { topics, level, types, isLoadingTopicsCatalog, topicsTotal, topicFilter } = useSelector(selectTopics);

  const searchResults = useSelector(selectSearchUnion);
  const topicCategories = useSelector(selectTopicsCategoriesUnion);
  const { currentUser } = useSelector(selectUsers);
  const { languagesFiltered } = useSelector(selectLocales);

  const [state, setState] = useState({
    searchingTopics: false,
    global: currentUser?.domainCompany?.global,
    currentTopicId: 0,
    listAllCompetence: [],
    competencesAndCategoriesQuantity: {},
    skillsNames: null,
    restoreFirstRequest: props.location.state?.comeFromHeader,
  });

  const initialQuerySearchParams = useMemo(() => parseSearchParams(document.location.search), []);

  const [topicsParams, setTopicsParams] = useState({
    ...initialAllState,
    ...initialQuerySearchParams,
    ...topicFilter,
  });

  useEffect(() => {
    if (!location.search) {
      setTopicsParams({ ...initialAllState, ...topicFilter });
    }
  }, [location.search]);

  const getSearchDebounced = useCallback(
    _debounce((...args) => {
      dispatch(getSearch(...args));
    }, 400),
    []
  );

  const handleFilterChange = event => {
    setTopicsParams({ ...topicsParams, ...event, page: 1 });
    dispatch(changeTopicFilter(Object.keys(event)[0], Object.values(event)[0]));
  };

  const handleFilterClear = () => {
    setTopicsParams(initialAllState);
    dispatch(clearTopicFilter());
  };

  const changePage = page => {
    window.scrollTo(0, 0);
    setTopicsParams({ ...topicsParams, page });
  };

  const handleChangeSort = sort => {
    setTopicsParams({ ...topicsParams, sort, page: 1, typeId: topicsParams?.typeId });
  };

  const onSearchChange = (value, type) => {
    getSearchDebounced(value, type === 'searchResult' ? '' : type, 'topic');
  };

  const onSearchSubmit = ({ value, type }) => {
    setTopicsParams({ ...topicsParams, ...initialSearchTopics, [type]: value });
  };

  const onSearchSuggestClick = (itemId, value, type) => {
    if (!type || type === 'name' || type === 'searchResult') {
      navigate(`/collection-topics/${itemId}`);
    } else {
      setTopicsParams({
        ...topicsParams,
        ...initialSearchTopics,
        searchResult: value,
        [type]: type === 'publisher' ? value : itemId,
      });
    }
  };

  const handleRangeDatesForCalendar = ({ startDate, endDate }) => {
    setTopicsParams({ ...topicsParams, startDate, endDate });
  };

  const handleSortReset = () => {
    setTopicsParams({ ...topicsParams, sort: initialTopicsParams.sort });
  };

  const getNearestFilterObject = () => {
    switch (topicsParams?.typeId) {
      case FILTER_TYPE_COURSES:
        return [{ name: t('nearest'), value: ['nearEvent,asc', 'nearEvent,desc'] }];
      case FILTER_TYPE_EVENTS:
        return [{ name: t('nearest'), value: ['state,asc&sort=start,asc', 'state,desc&sort=start,desc'] }];
      default:
        break;
    }

    return [];
  };

  useEffect(() => {
    if (controllerTopics) controllerTopics.abort();
    controllerTopics = new AbortController();

    dispatch(getTopics(topicsParams, () => {}, controllerTopics.signal));
  }, [topicsParams]);

  useLayoutEffect(() => {
    if (!Utils.isOnline()) return;

    // dispatch(getUserDomainCompanyImages()); Нужен рефакторинг
    if (languagesFiltered?.length === 0) dispatch(getLanguagesFiltered());
    if (!types) dispatch(getTopicTypes());
    if (!level) dispatch(getTopicLevel());
    // в компаннте ModalFlower дергается эта ручка getTopicCategories закомментировал чтоб не дублировался код
    // if (!topics) dispatch(getTopicCategories(currentUser?.domainCompany?.global));

    return () => dispatch(clearTopics());
  }, []);

  const isActiveFilterSkillIds = Boolean(
    topicsParams.skillIds && Array.isArray(topicsParams.skillIds) && topicsParams.skillIds.length > 0
  );

  const isFilterNotEmpty =
    topicsParams &&
    (topicsParams.name ||
      topicsParams.authorName ||
      topicsParams.publisher ||
      topicsParams.tagName ||
      topicsParams.authorId ||
      topicsParams.tagId ||
      topicsParams.skillId ||
      isActiveFilterSkillIds ||
      topicsParams.skillName ||
      topicsParams.searchResult);

  // const labelFilter = isFilterNotEmpty
  //   ? topicsParams.authorName || topicsParams.authorId
  //     ? t('author')
  //     : topicsParams.tagName || topicsParams.tagId
  //       ? t('tag')
  //       : topicsParams.publisher
  //         ? t('company')
  //         : topicsParams.name
  //           ? t('name')
  //           : topicsParams.skillName || topicsParams.skillId || isActiveFilterSkillIds
  //             ? t('skill')
  //             : topicsParams.searchResult
  //               ? ''
  //               : ''
  //   : null;

  const searchTypes = [
    { id: '', type: 'searchResult', searchType: 'searchResult', tagType: 'searchResult', name: t('all') },
    { id: 1, type: 'name', searchType: 'TOPIC', tagType: 'name', name: t('selectName') },
    { id: 2, type: 'authorName', searchType: 'AUTHOR', tagType: 'authorId', name: t('selectAuthor') },
    { id: 3, type: 'publisher', searchType: 'PUBLISHER', tagType: 'publisher', name: t('selectCompany') },
    { id: 4, type: 'tagName', searchType: 'TAG', tagType: 'tagId', name: t('selectTag') },
    { id: 5, type: 'skillName', searchType: 'SKILL', tagType: 'skillId', name: t('selectSkill') },
  ];

  const showStartSearch = topicsParams.typeId === 1 || topicsParams.typeId === 2;

  const sortTypes = [
    {
      name: t('popularity'),
      value: [
        'positions.position,asc&sort=topicShortTermPopularities.shortTermPopularity,desc&sort=id,asc',
        'positions.position,desc&sort=topicShortTermPopularities.shortTermPopularity,asc&sort=id,desc',
      ],
      type: SortTypeHelp.COUNT,
    },
    {
      name: t('rating'),
      value: ['rates.rate,desc&sort=id,asc', 'rates.rate,asc&sort=id,desc'],
      type: SortTypeHelp.COUNT,
    },
    ...(showStartSearch ? getNearestFilterObject() : []),
    {
      name: t('date'),
      value: [
        'positions.position,asc&sort=createdDate,desc&sort=id,asc',
        'positions.position,desc&sort=createdDate,asc&sort=id,desc',
      ],
      type: SortTypeHelp.DATE,
    },
    {
      name: t('startDate'),
      value: [
        'positions.position,asc&sort=startDate,asc&sort=id,asc',
        'positions.position,desc&sort=startDate,desc&sort=id,desc',
      ],
      type: SortTypeHelp.DATE,
    },
  ];

  // const labelActualSearchType = topicsParams?.searchResult || '';

  // const labelSearchText =
  //   labelActualSearchType.length > 20 ? `${labelActualSearchType.substring(0, 20)}... ` : labelActualSearchType;

  const areDatesSelected = !!(topicsParams.startDate || topicsParams.endDate);
  return (
    <Content className={css['App-content']}>
      <div className={css['CollectionMaterials-header-row']}>
        <div className={css['CollectionMaterials-header-text']}>
          <Title level={1} data-qa='eventCalendarTitle'>
            {t('eventCalendar')}
          </Title>
        </div>

        <div className={css['MyCollection-header-search']}>
          <ContextSearch
            dataQa='collectionTopicSearch'
            navigate={navigate}
            onChange={onSearchChange}
            onSearchSubmit={onSearchSubmit}
            onSearchSuggestClick={onSearchSuggestClick}
            searchResults={searchResults}
            initialQuerySearchParams={initialQuerySearchParams}
            searchTypes={searchTypes}
            isFiltered={isFilterNotEmpty}
          />
        </div>
      </div>
      <TopicFilter
        filter={topicsParams}
        competencesAndCategoriesQuantity={args => setState({ ...state, competencesAndCategoriesQuantity: args })}
        topicTypes={types}
        topicCategories={topicCategories}
        topicLevel={level}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
        onSortReset={handleSortReset}
        domainCompany={currentUser.domainCompany}
        currentUser={currentUser}
        myCollection={false}
        topicLanguages={languagesFiltered}
        showAllCategory
        hasEvent
      />

      {/* <div className={css['CollectionMaterials-sort-block-sortBy']}>{t('sortBy')}</div> */}
      <div className={css['CollectionMaterials-topicDate-sort-block']}>
        {(topics?.length > 0 || areDatesSelected) && (
          <CustomRangeDatePicker
            onDatesSelect={handleRangeDatesForCalendar}
            startDate={topicsParams.startDate}
            endDate={topicsParams.endDate}
          />
        )}
        {!isLoadingTopicsCatalog && topics?.length > 0 && (
          <Sort
            isLoading={isLoadingTopicsCatalog}
            handleChangeSort={handleChangeSort}
            activeSort={topicsParams.sort}
            sortTypes={sortTypes}
            className={css['CollectionMaterials-sort']}
          />
        )}
      </div>

      <TopicCards
        isLoading={isLoadingTopicsCatalog}
        topics={topics}
        isAddMaterialButton
        isFilterNotEmpty={isFilterNotEmpty}
        searchText={topicsParams.searchResult}
      />

      {!isLoadingTopicsCatalog && topics?.length > 0 && (
        <Pagination
          current={topicsParams.page}
          total={topicsTotal}
          pageSize={initialTopicsParams.size}
          onChange={changePage}
          showSizeChanger={false}
          className={css['CollectionMaterials-pagination']}
          responsive
          hideOnSinglePage
        />
      )}
    </Content>
  );
};

CalendarMaterials.propTypes = {
  isLoadingTopicsCatalog: PropTypes.bool,
  topics: PropTypes.array,
  topicsTotal: PropTypes.number,
};

export default CalendarMaterials;
